import { useState, useEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast, Toaster } from "sonner";
import { ArrowLeft, ArrowRight, Upload, Check, Loader2, FileText, Trash2, ShieldCheck, X, MapPin, Sparkles } from "lucide-react";
import { useAuth } from "../context/AuthContext";

const MATERIALS = ["TMT Bars", "Cement", "Structural Steel", "Wire Mesh", "PCC / RMC", "Aggregates", "Sand", "Ply / Boards", "Sanitary", "Electricals"];
const BIZ_TYPES = [["private_limited", "Private Limited"], ["llp", "LLP"], ["proprietorship", "Proprietorship"], ["partnership", "Partnership"], ["huf", "HUF"], ["other", "Other"]];
const ROLE_SUBS = {
  buyer: [["super_stockist", "Super Stockist"], ["wholesaler", "Wholesaler"], ["dealer", "Dealer / Agent"], ["introducer", "Introducer / Referrer"]],
  seller: [["manufacturer", "Manufacturer"], ["trader", "Trader"], ["stockist", "Stockist"]],
  custodian: [["custodian", "Custodian / Guarantor"]],
};

/**
 * Master Registration & Profiling — 4-step obsidian/gold form.
 */
export default function Onboarding() {
  const { user, api, refresh } = useAuth();
  const navigate = useNavigate();
  const [state, setState] = useState(null);
  const [busy, setBusy] = useState(false);
  const [step, setStep] = useState(0);

  const [form, setForm] = useState({
    company_name: "", business_type: "private_limited", year_of_incorporation: "",
    gst_number: "", pan_number: "", state: "", address: "", phone: "",
    material_focus: [], years_of_operation: "", annual_turnover_inr: "",
    role: "buyer", role_sub: "", role_specific: {},
    authorized_states: [], accept_terms: false,
  });

  const [statesList, setStatesList] = useState([]);
  const [feeBreakup, setFeeBreakup] = useState(null);
  useEffect(() => {
    api.get("/states").then((r) => setStatesList(r.data || [])).catch(() => setStatesList([]));
  }, [api]);
  useEffect(() => {
    const n = Math.max(1, form.authorized_states.length || 1);
    let dead = false;
    api.get(`/certificates/fee-calc?states=${n}`)
      .then((r) => { if (!dead) setFeeBreakup(r.data); })
      .catch(() => { if (!dead) setFeeBreakup(null); });
    return () => { dead = true; };
  }, [api, form.authorized_states.length]);

  const load = useCallback(async () => {
    try {
      const r = await api.get("/onboarding/state");
      setState(r.data);
      const p = r.data.profile || {};
      const ob = r.data.onboarding || {};
      setForm((f) => ({
        ...f,
        company_name: p.business_name || f.company_name,
        gst_number: p.gst_number || f.gst_number,
        pan_number: p.pan_number || f.pan_number,
        state: p.state || f.state,
        address: p.address || f.address,
        phone: p.phone || f.phone,
        business_type: p.business_type || f.business_type,
        year_of_incorporation: p.year_of_incorporation || f.year_of_incorporation,
        material_focus: p.material_focus || [],
        years_of_operation: p.years_of_operation || "",
        annual_turnover_inr: p.annual_turnover_inr || "",
        authorized_states: Array.isArray(p.authorized_states) && p.authorized_states.length
          ? p.authorized_states
          : (p.state ? [p.state] : f.authorized_states),
        role: ob.role || ob.eoi_role_interest || (r.data.user?.account_type === "seller" ? "seller" : "buyer"),
        role_sub: ob.role_sub || r.data.user?.buyer_category || "",
      }));
    } catch (e) { toast.error("Could not load onboarding state"); }
  }, [api]);
  useEffect(() => { load(); }, [load]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleMat = (m) => setForm((f) => ({ ...f, material_focus: f.material_focus.includes(m) ? f.material_focus.filter((x) => x !== m) : [...f.material_focus, m] }));

  const canAdvance = () => {
    if (step === 0) return form.company_name.trim() && form.pan_number.trim() && form.state.trim() && form.phone.trim();
    if (step === 1) return form.role && form.role_sub;
    if (step === 2) return true;
    return true;
  };

  const submitAll = async () => {
    if (!form.accept_terms) { toast.error("Please accept the terms to submit"); return; }
    setBusy(true);
    try {
      const payload = {
        ...form,
        year_of_incorporation: form.year_of_incorporation ? Number(form.year_of_incorporation) : null,
        years_of_operation: form.years_of_operation ? Number(form.years_of_operation) : null,
        annual_turnover_inr: form.annual_turnover_inr ? Number(form.annual_turnover_inr) : null,
      };
      const r = await api.post("/onboarding/submit", payload);
      toast.success(r.data.docs_complete ? "Registration submitted — under Admin review" : "Registration saved — upload the remaining documents to complete");
      try { await refresh?.(); } catch {}
      navigate("/account");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Submission failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-[#070707] text-[#F5F5F0] px-6 md:px-12 py-16">
      <Toaster position="bottom-right" theme="dark" richColors />
      <div className="max-w-[1100px] mx-auto">
        <Link to="/account" data-testid="ob-back" className="inline-flex items-center gap-2 text-[#8A8A82] hover:text-[#B87333] text-xs uppercase tracking-[0.2em] font-grotesk mb-6">
          <ArrowLeft className="w-4 h-4" /> Back to Account
        </Link>

        {/* Hero */}
        <div className="mb-10 border-b border-white/10 pb-8">
          <div className="overline text-[#B87333] mb-2">Vinci Registry · Master File</div>
          <h1 className="font-serif text-4xl md:text-5xl">Universal Registration</h1>
          <p className="font-body text-sm text-[#8A8A82] mt-3 max-w-2xl">
            Complete your onboarding to unlock the trade desk, quotations, credit line, and the Certificate of Authorisation.
            Every field is auto-saved to your Vinci Registry file.
          </p>
          <div className="mt-6 flex items-center gap-4">
            <div className="flex-1 h-1 bg-white/8 rounded-full overflow-hidden">
              <div className="h-full bg-gradient-to-r from-[#B87333] to-[#E4C88E] transition-all"
                   style={{ width: `${state?.completion_pct ?? 20}%` }} data-testid="ob-progress-bar" />
            </div>
            <span className="text-[10px] text-[#B87333] font-grotesk uppercase tracking-[0.24em]" data-testid="ob-progress-pct">
              {state?.completion_pct ?? 20}% complete
            </span>
          </div>
        </div>

        {/* Step tabs */}
        <div className="grid grid-cols-4 gap-2 mb-8">
          {[["Company", 0], ["Role", 1], ["Documents", 2], ["Review", 3]].map(([label, i]) => (
            <button key={i} data-testid={`ob-step-${i}`} onClick={() => setStep(i)}
              className={`px-3 py-3 border rounded-sm text-[10px] uppercase tracking-[0.24em] font-grotesk transition-colors ${step === i ? "border-[#B87333] text-[#B87333] bg-[#B87333]/5" : "border-white/10 text-[#8A8A82] hover:border-white/20"}`}>
              <span className="tabular-nums text-[#B87333] mr-2">{String(i + 1).padStart(2, "0")}</span>{label}
            </button>
          ))}
        </div>

        {step === 0 && <StepCompany form={form} set={set} toggleMat={toggleMat} statesList={statesList} feeBreakup={feeBreakup} />}
        {step === 1 && <StepRole form={form} set={set} />}
        {step === 2 && <StepDocuments api={api} state={state} onChanged={load} />}
        {step === 3 && <StepReview form={form} state={state} set={set} onSubmit={submitAll} busy={busy} feeBreakup={feeBreakup} />}

        {/* Nav */}
        <div className="mt-10 flex justify-between">
          <button data-testid="ob-prev" disabled={step === 0} onClick={() => setStep((s) => Math.max(0, s - 1))}
            className="rounded-full border border-white/20 px-5 py-2 text-xs uppercase tracking-[0.2em] disabled:opacity-30">Back</button>
          {step < 3 && (
            <button data-testid="ob-next" disabled={!canAdvance()} onClick={() => setStep((s) => Math.min(3, s + 1))}
              className="rounded-full bg-[#B87333] text-black px-6 py-2 text-xs uppercase tracking-[0.24em] flex items-center gap-2 hover:brightness-110 disabled:opacity-40">
              Next <ArrowRight className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

/* -------------- Steps -------------- */
function StepCompany({ form, set, toggleMat, statesList, feeBreakup }) {
  const MAX = feeBreakup?.max_states || 5;
  const toggleAuthState = (s) => {
    const list = form.authorized_states || [];
    if (list.includes(s)) return set("authorized_states", list.filter((x) => x !== s));
    if (list.length >= MAX) { toast.warning(`Maximum ${MAX} states allowed`); return; }
    set("authorized_states", [...list, s]);
  };
  return (
    <div className="space-y-6" data-testid="ob-step-company">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Field label="Legal Company Name *"><Input testid="ob-company" value={form.company_name} onChange={(v) => set("company_name", v)} /></Field>
        <Field label="Business Type">
          <Select testid="ob-biztype" value={form.business_type} onChange={(v) => set("business_type", v)}>
            {BIZ_TYPES.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
          </Select>
        </Field>
        <Field label="GSTIN"><Input testid="ob-gst" value={form.gst_number} onChange={(v) => set("gst_number", v.toUpperCase())} /></Field>
        <Field label="PAN Number *"><Input testid="ob-pan" value={form.pan_number} onChange={(v) => set("pan_number", v.toUpperCase())} /></Field>
        <Field label="Registered State *"><Input testid="ob-state" value={form.state} onChange={(v) => set("state", v)} /></Field>
        <Field label="Phone *"><Input testid="ob-phone" value={form.phone} onChange={(v) => set("phone", v)} /></Field>
        <Field label="Registered Address *" wide><Input testid="ob-addr" value={form.address} onChange={(v) => set("address", v)} /></Field>
        <Field label="Year of Incorporation"><Input testid="ob-yoi" type="number" value={form.year_of_incorporation} onChange={(v) => set("year_of_incorporation", v)} /></Field>
        <Field label="Years of Operation"><Input testid="ob-yop" type="number" value={form.years_of_operation} onChange={(v) => set("years_of_operation", v)} /></Field>
        <Field label="Annual Turnover (INR)"><Input testid="ob-turnover" type="number" value={form.annual_turnover_inr} onChange={(v) => set("annual_turnover_inr", v)} /></Field>
      </div>

      {/* -------- Authorized States (Certificate of Authorisation coverage) -------- */}
      <div className="border-t border-white/10 pt-6" data-testid="ob-authstates-section">
        <div className="flex items-baseline justify-between mb-3">
          <div>
            <div className="text-[10px] uppercase tracking-[0.22em] text-[#B87333] font-grotesk">Authorized States · Certificate of Authorisation</div>
            <div className="text-[11px] text-[#8A8A82] font-body mt-1 max-w-xl">
              Pick every state you intend to operate in. The 1st state is included in the base authorisation fee, and every additional state is offered at a flat <span className="text-[#D8B23E]">50% off the base rate</span>.
            </div>
          </div>
          <span className="text-[10px] font-grotesk uppercase tracking-[0.22em] text-[#8A8A82]" data-testid="ob-authstates-count">
            {form.authorized_states.length}/{MAX} selected
          </span>
        </div>

        {form.authorized_states.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mb-3" data-testid="ob-authstates-picked">
            {form.authorized_states.map((s) => (
              <button key={s} type="button" onClick={() => toggleAuthState(s)}
                      data-testid={`ob-authstate-picked-${s.replace(/\s+/g, "-")}`}
                      className="inline-flex items-center gap-1 border border-[#B87333] text-[#B87333] bg-[#B87333]/10 rounded-full px-3 py-1 text-[11px] font-grotesk">
                <MapPin className="w-3 h-3"/> {s} <X className="w-3 h-3"/>
              </button>
            ))}
          </div>
        )}

        <div className="max-h-52 overflow-y-auto border border-white/10 rounded-sm p-3 bg-white/[0.02]" data-testid="ob-authstates-picker">
          <div className="flex flex-wrap gap-1.5">
            {(statesList || []).map((s) => {
              const active = form.authorized_states.includes(s);
              const atCap = !active && form.authorized_states.length >= MAX;
              return (
                <button key={s} type="button" disabled={atCap}
                        onClick={() => toggleAuthState(s)}
                        data-testid={`ob-authstate-opt-${s.replace(/\s+/g, "-")}`}
                        className={`rounded-full px-3 py-1 text-[11px] font-grotesk border transition-colors ${
                          active
                            ? "border-[#B87333] text-[#B87333] bg-[#B87333]/10"
                            : atCap
                              ? "border-white/5 text-white/20 cursor-not-allowed"
                              : "border-white/10 text-[#C9C9C2] hover:border-white/30"
                        }`}>
                  {active ? "✓ " : ""}{s}
                </button>
              );
            })}
          </div>
        </div>

        {feeBreakup && (
          <div className="mt-4"><OnboardingFeeCard breakup={feeBreakup} /></div>
        )}
      </div>

      <div>
        <div className="text-[10px] uppercase tracking-[0.22em] text-[#8A8A82] font-grotesk mb-3">Material Focus</div>
        <div className="flex flex-wrap gap-2">
          {MATERIALS.map((m) => {
            const active = form.material_focus.includes(m);
            return <button key={m} type="button" data-testid={`ob-mat-${m}`} onClick={() => toggleMat(m)}
              className={`rounded-full px-4 py-1.5 text-[11px] font-grotesk tracking-[0.15em] uppercase border transition-colors ${active ? "border-[#B87333] text-[#B87333] bg-[#B87333]/10" : "border-white/15 text-[#C9C9C2] hover:border-white/30"}`}>
              {active ? "✓ " : ""}{m}
            </button>;
          })}
        </div>
      </div>
    </div>
  );
}

function StepRole({ form, set }) {
  const subs = ROLE_SUBS[form.role] || [];
  return (
    <div className="space-y-6" data-testid="ob-step-role">
      <div>
        <div className="text-[10px] uppercase tracking-[0.22em] text-[#8A8A82] font-grotesk mb-3">Which role best describes you?</div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {[["buyer", "Buyer", "I purchase materials for projects, dealerships or resale"],
            ["seller", "Seller / Supplier", "I supply materials to buyers on the Vinci network"],
            ["custodian", "Custodian / Guarantor", "I guarantee onboarding for dependent partners"]].map(([k, label, desc]) => (
            <button key={k} type="button" data-testid={`ob-role-${k}`}
              onClick={() => { set("role", k); set("role_sub", ROLE_SUBS[k][0][0]); }}
              className={`text-left border rounded-sm p-4 transition-colors ${form.role === k ? "border-[#B87333] bg-[#B87333]/5" : "border-white/15 hover:border-white/30"}`}>
              <div className="flex items-center gap-2 mb-1">
                {form.role === k ? <Check className="w-4 h-4 text-[#B87333]" /> : <span className="w-4 h-4 rounded-full border border-white/30" />}
                <div className="font-serif text-lg text-[#F5F5F0]">{label}</div>
              </div>
              <div className="text-[11px] text-[#8A8A82] font-body ml-6">{desc}</div>
            </button>
          ))}
        </div>
      </div>
      <div>
        <div className="text-[10px] uppercase tracking-[0.22em] text-[#8A8A82] font-grotesk mb-3">Sub-category *</div>
        <div className="flex flex-wrap gap-2">
          {subs.map(([k, l]) => (
            <button key={k} type="button" data-testid={`ob-sub-${k}`} onClick={() => set("role_sub", k)}
              className={`rounded-full px-4 py-1.5 text-[11px] font-grotesk tracking-[0.15em] uppercase border ${form.role_sub === k ? "border-[#B87333] text-[#B87333] bg-[#B87333]/10" : "border-white/15 text-[#C9C9C2] hover:border-white/30"}`}>
              {l}
            </button>
          ))}
        </div>
      </div>
      <RoleSpecific form={form} set={set} />
    </div>
  );
}

function RoleSpecific({ form, set }) {
  const rs = form.role_specific || {};
  const setRS = (k, v) => set("role_specific", { ...rs, [k]: v });

  if (form.role === "buyer") {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Field label="Monthly Purchase Volume (₹)"><Input testid="ob-vol" type="number" value={rs.monthly_purchase_inr || ""} onChange={(v) => setRS("monthly_purchase_inr", v)} /></Field>
        <Field label="Number of Projects/Sites"><Input testid="ob-projects" type="number" value={rs.projects_count || ""} onChange={(v) => setRS("projects_count", v)} /></Field>
        <Field label="Delivery Radius (km)"><Input testid="ob-radius" type="number" value={rs.delivery_radius_km || ""} onChange={(v) => setRS("delivery_radius_km", v)} /></Field>
        <Field label="Payment Terms Preference">
          <Select testid="ob-terms" value={rs.payment_terms || ""} onChange={(v) => setRS("payment_terms", v)}>
            <option value="">— Select —</option>
            <option value="advance">100% Advance</option>
            <option value="split">50% Advance / 50% on Dispatch</option>
            <option value="net_30">Net 30</option>
            <option value="net_60">Net 60</option>
          </Select>
        </Field>
      </div>
    );
  }
  if (form.role === "seller") {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Field label="Manufacturing Capacity (MT / month)"><Input testid="ob-cap" type="number" value={rs.capacity_mt_month || ""} onChange={(v) => setRS("capacity_mt_month", v)} /></Field>
        <Field label="Number of Plants / Depots"><Input testid="ob-plants" type="number" value={rs.plants_count || ""} onChange={(v) => setRS("plants_count", v)} /></Field>
        <Field label="Brands / SKUs Supplied" wide><Input testid="ob-brands" value={rs.brands || ""} onChange={(v) => setRS("brands", v)} placeholder="TATA Tiscon, JSW Neosteel, Ambuja Cement…" /></Field>
        <Field label="Certifications">
          <Input testid="ob-certs" value={rs.certifications || ""} onChange={(v) => setRS("certifications", v)} placeholder="BIS, ISO 9001, ISI…" />
        </Field>
      </div>
    );
  }
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <Field label="Number of Dependents you plan to guarantee"><Input testid="ob-deps" type="number" value={rs.dependents_count || ""} onChange={(v) => setRS("dependents_count", v)} /></Field>
      <Field label="Guarantee Capital (₹)"><Input testid="ob-cap-guar" type="number" value={rs.guarantee_capital || ""} onChange={(v) => setRS("guarantee_capital", v)} /></Field>
      <Field label="Introduction Segment" wide><Input testid="ob-segment" value={rs.segment || ""} onChange={(v) => setRS("segment", v)} placeholder="Retail dealers · MSME projects · Bulk contractors…" /></Field>
    </div>
  );
}

function StepDocuments({ api, state, onChanged }) {
  const slots = state?.doc_slots || [];
  const docs = state?.documents || [];
  const [busy, setBusy] = useState(null);

  const upload = async (slot, file) => {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error("File must be under 5 MB"); return; }
    setBusy(slot);
    try {
      const fd = new FormData(); fd.append("slot", slot); fd.append("file", file);
      await api.post("/onboarding/documents", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Document uploaded"); onChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || "Upload failed"); }
    finally { setBusy(null); }
  };
  const remove = async (id) => {
    if (!window.confirm("Remove this document?")) return;
    try { await api.delete(`/onboarding/documents/${id}`); toast.success("Removed"); onChanged(); }
    catch { toast.error("Failed"); }
  };

  const bySlot = (k) => docs.filter((d) => d.slot === k);

  return (
    <div className="space-y-4" data-testid="ob-step-documents">
      <div className="text-[11px] text-[#8A8A82] font-body mb-2">
        Upload PDF, JPG or PNG · max 5 MB per file. Documents are stored in Vinci's secure vault and linked to your Admin file for review.
      </div>
      {slots.map((s) => {
        const list = bySlot(s.key);
        return (
          <div key={s.key} data-testid={`ob-doc-slot-${s.key}`} className="border border-white/10 rounded-sm p-5 bg-white/[0.02]">
            <div className="flex items-center justify-between mb-3">
              <div>
                <div className="font-serif text-lg text-[#F5F5F0]">{s.label}{s.required ? <span className="ml-2 text-[10px] text-[#B87333]">REQUIRED</span> : null}</div>
                <div className="text-[10px] text-[#8A8A82] font-body">{s.multi ? "Multiple files allowed" : "Single file · re-uploading replaces the previous copy"}</div>
              </div>
              <label className="rounded-full bg-[#B87333] text-black px-4 py-2 text-[11px] uppercase tracking-[0.2em] font-grotesk flex items-center gap-2 cursor-pointer hover:brightness-110">
                {busy === s.key ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
                {list.length ? (s.multi ? "Add" : "Replace") : "Upload"}
                <input data-testid={`ob-doc-input-${s.key}`} type="file" accept="application/pdf,image/png,image/jpeg" className="hidden"
                  onChange={(e) => upload(s.key, e.target.files?.[0])} />
              </label>
            </div>
            {list.length > 0 && (
              <div className="space-y-1.5">
                {list.map((d) => (
                  <div key={d.id} className="flex items-center justify-between text-xs px-3 py-2 rounded bg-white/[0.03]">
                    <div className="flex items-center gap-2 text-[#C9C9C2]"><FileText className="w-4 h-4 text-[#2FB999]" /><span>{d.filename}</span><span className="text-[#8A8A82]">· {(d.size_bytes/1024).toFixed(0)} KB</span></div>
                    <button data-testid={`ob-doc-del-${d.id}`} onClick={() => remove(d.id)} className="text-[#8A8A82] hover:text-[#C0392B]"><Trash2 className="w-4 h-4" /></button>
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function StepReview({ form, state, set, onSubmit, busy, feeBreakup }) {
  const docs = state?.documents || [];
  return (
    <div className="space-y-6" data-testid="ob-step-review">
      <div className="border border-[#B87333]/30 rounded-sm p-6 bg-white/[0.02]">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Info label="Company" value={form.company_name || "—"} />
          <Info label="PAN" value={form.pan_number || "—"} />
          <Info label="GSTIN" value={form.gst_number || "—"} />
          <Info label="Registered State" value={form.state || "—"} />
          <Info label="Role" value={`${form.role} · ${form.role_sub}`.toUpperCase()} />
          <Info label="Material Focus" value={form.material_focus.join(", ") || "—"} />
          <Info label="Years of Ops" value={form.years_of_operation || "—"} />
          <Info label="Documents" value={`${docs.length} uploaded`} />
          <Info label="Authorized States" value={form.authorized_states?.length ? `${form.authorized_states.length} · ${form.authorized_states.join(", ")}` : "—"} wide />
        </div>
      </div>
      {feeBreakup && <OnboardingFeeCard breakup={feeBreakup} />}
      <label className="flex items-start gap-3 text-xs text-[#C9C9C2] font-body cursor-pointer">
        <input type="checkbox" data-testid="ob-terms" checked={form.accept_terms} onChange={(e) => set("accept_terms", e.target.checked)}
          className="mt-0.5 accent-[#B87333]" />
        <span>I confirm the information above is accurate and I accept the Vinci Ecosystem terms, the Indemnity Bond and the Data Retention Policy. I understand the Certificate of Authorisation fee shown above is payable to Vinci Inc. upon issuance and will be raised via secure PhonePe and Razorpay links.</span>
      </label>
      <button data-testid="ob-submit" disabled={busy || !form.accept_terms} onClick={onSubmit}
        className="w-full rounded-full bg-[#B87333] text-black px-6 py-3 text-xs uppercase tracking-[0.28em] font-grotesk flex items-center justify-center gap-2 hover:brightness-110 disabled:opacity-40">
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4" />}
        Submit for Admin Approval
      </button>
    </div>
  );
}

/* -------------- Reusables -------------- */
function Field({ label, wide, children }) {
  return <label className={`block ${wide ? "md:col-span-2" : ""}`}>
    <div className="text-[10px] uppercase tracking-[0.2em] text-[#8A8A82] font-grotesk mb-1.5">{label}</div>
    {children}
  </label>;
}
function Input({ value, onChange, testid, placeholder, type = "text" }) {
  return <input data-testid={testid} type={type} value={value ?? ""} placeholder={placeholder}
    onChange={(e) => onChange(e.target.value)}
    className="w-full bg-white/5 border border-white/15 rounded-sm px-3 py-2.5 text-sm text-[#F5F5F0] outline-none focus:border-[#B87333]" />;
}
function Select({ value, onChange, testid, children }) {
  return <select data-testid={testid} value={value} onChange={(e) => onChange(e.target.value)}
    className="w-full bg-white/5 border border-white/15 rounded-sm px-3 py-2.5 text-sm text-[#F5F5F0] outline-none focus:border-[#B87333]">{children}</select>;
}
function Info({ label, value, wide }) {
  return <div className={wide ? "md:col-span-4 col-span-2" : ""}>
    <div className="text-[10px] uppercase tracking-[0.22em] text-[#8A8A82] font-grotesk mb-1">{label}</div>
    <div className="text-sm text-[#F5F5F0]">{value}</div>
  </div>;
}


/**
 * OnboardingFeeCard — sophisticated obsidian panel that mirrors the admin
 * fee breakup with a subtle gold gradient. Real-time reflection of what the
 * partner will owe once their Certificate of Authorisation is issued.
 */
function OnboardingFeeCard({ breakup }) {
  const rupee = (n) => `₹${Number(n || 0).toLocaleString("en-IN")}`;
  const isMulti = (breakup.additional_states || 0) > 0;
  return (
    <div data-testid="ob-fee-breakup"
         className="relative overflow-hidden border border-[#B87333]/30 rounded-sm p-5 bg-gradient-to-br from-[#0e0a06] via-[#0a0a0a] to-[#0e0a06]">
      <div className="absolute inset-0 pointer-events-none opacity-[0.06]"
           style={{ backgroundImage: "radial-gradient(circle at 15% 0%, #D8B23E 0%, transparent 55%)" }} />
      <div className="relative">
        <div className="flex items-baseline justify-between mb-3">
          <div className="text-[10px] uppercase tracking-[0.24em] text-[#B87333] font-grotesk flex items-center gap-2">
            <Sparkles className="w-3 h-3"/> Total Certification Fee · Live
          </div>
          {isMulti && (
            <span className="font-grotesk text-[10px] uppercase tracking-[0.24em] text-[#D8B23E]" data-testid="ob-fee-discount-badge">
              ✦ Multi-state · {breakup.discount_pct}% off
            </span>
          )}
        </div>
        <div className="space-y-2 font-body text-[13px] text-[#C9C9C2]">
          <div className="flex justify-between">
            <span>Base authorisation · 1st state</span>
            <span className="tabular-nums text-[#F5F5F0]" data-testid="ob-fee-base">{rupee(breakup.base_fee_inr)}</span>
          </div>
          {isMulti ? (
            <div className="flex justify-between">
              <span>+ {breakup.additional_states} state(s) × <span className="text-[#D8B23E]">{rupee(breakup.per_additional_state_inr)}</span> <span className="text-[#8A8A82]">(50% off base)</span></span>
              <span className="tabular-nums text-[#F5F5F0]" data-testid="ob-fee-add">{rupee(breakup.additional_full_inr)}</span>
            </div>
          ) : (
            <div className="text-[11px] text-[#8A8A82] italic pt-1 leading-relaxed">
              <span className="text-[#D8B23E] not-italic font-semibold">Insider tip.</span> Every additional Authorized State beyond the first is offered at <span className="text-[#D8B23E] not-italic">50% off the base rate</span> — grow your coverage without doubling your fee.
            </div>
          )}
        </div>
        <div className="mt-4 pt-4 border-t border-white/10 flex items-baseline justify-between">
          <span className="font-grotesk text-[11px] uppercase tracking-[0.28em] text-[#B87333]">Payable on issuance</span>
          <span className="font-serif text-3xl text-[#F5F5F0] tabular-nums" data-testid="ob-fee-total">{rupee(breakup.total_inr)}</span>
        </div>
      </div>
    </div>
  );
}
